import { isBrowser } from '@/utils/environment';
import { Analytics } from '@gik/analytics';
import { AnalyticsEvents } from '@gik/analytics/utils/Events';
import { logger } from '@gik/analytics/utils/logger';
import { updateUser, useUser } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';
import type { User } from '@gik/core/models/gik/User';
import { useUserStore } from '@gik/core/store/UserStore';
import type { FormSchemaEntry } from '@gik/ui/Form';
import { Form } from '@gik/ui/Form';
import { UI } from '@gik/ui/UIManager';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';

export type JITRequirement = 'user-firstName' | 'user-lastName'; // TODO: add other requirements here

/**
 * Just in time data collection for requirements
 *
 * @param requirements array of <b>memoized</b> requirements to be collected
 * @param options customize the behavior of the JIT Collection
 * @returns function to launch a modal to collect any requirements.
 * - If requirements are not yet loaded, null will be returned.
 * - The function will return true if all missing data is provided by the user.
 * - If no requirements are missing, the function will immediately return true.
 */
export default function useJITCollect(
  requirements: JITRequirement[],
  options: {
    closable: boolean;
    initiatedOn: string;
  } = {
    closable: false,
    initiatedOn: 'appload',
  }
): () => Promise<boolean> | null {
  const { t } = useTranslation();
  const userId = useUserStore(store => store.id);
  const requiresFirstName = requirements.indexOf('user-firstName') >= 0;
  const requiresLastName = requirements.indexOf('user-lastName') >= 0;

  // only retrieve user for firstName requirement, other data for other checks can be added below
  const { data: user, mutate: mutateUser } = useUser((requiresFirstName || requiresLastName) && isBrowser() ? userId : null);

  let isLoading = false;
  let areRequirementsMissing = false;

  // TODO: add a better way to manage title and description
  let title = t(translationKeys.defaultTitle);
  let description = t(translationKeys.defaultDescription);

  // individual requirements missing
  let isFirstNameRequirementMissing = false;
  let isLastNameRequirementMissing = false;

  // check if first name is missing, other checks can be added below
  if (requiresFirstName) {
    if (user) {
      if (!user.firstName) {
        isFirstNameRequirementMissing = true;
        areRequirementsMissing = true;
        title = t(translationKeys.completeUserProfileTitle);
        description = t(translationKeys.completeUserProfileFirstNameDescription);
      }
    } else {
      isLoading = true;
    }
  }

  if (requiresLastName) {
    if (user) {
      if (!user.lastName) {
        isLastNameRequirementMissing = true;
        areRequirementsMissing = true;
        title = t(translationKeys.completeUserProfileTitle);
        description = t(translationKeys.completeUserProfileLastNameDescription);
      }
    } else {
      isLoading = true;
    }
  }

  // TODO: if we need multiple schemas, consider adding a multi-step modal.

  // ensure the schema is only generated once based on which requirements are missing
  const memoizedSchema: FormSchemaEntry[] = React.useMemo(() => {
    const schema: FormSchemaEntry[] = [];
    // TODO: take from user settings
    if (isFirstNameRequirementMissing) {
      // TODO: remove duplication between here, signup form and user settings
      schema.push({
        name: 'firstName',
        type: 'text',
        minLength: 2,
        maxLength: 127,
        required: true,
        props: {
          placeholder: 'Your First name',
        },
      });
    }

    if (isLastNameRequirementMissing) {
      // TODO: remove duplication between here, signup form and user settings
      schema.push({
        name: 'lastName',
        type: 'text',
        minLength: 2,
        maxLength: 127,
        required: true,
        props: {
          placeholder: 'Your Last name',
        },
      });
    }
    // TODO: add checks for other requirements here

    return schema;
  }, [isFirstNameRequirementMissing, isLastNameRequirementMissing]);

  const launchModal = React.useCallback(() => {
    if (areRequirementsMissing && !isLoading) {
      Analytics.fireEvent(AnalyticsEvents.JITDataCollectionModalOpened, {
        requirements: requirements.join(','),
        initiatedOn: options.initiatedOn,
      });
      return UI.dialog(
        ({ close }) => {
          // Note: values are unknown as it is based on the schema generated by the missing requirements
          async function handleSubmit(values: unknown) {
            try {
              if (isFirstNameRequirementMissing || isLastNameRequirementMissing) {
                const firstAndLastNameFormValues = values as { firstName: string; lastName: string};
                const updatedUser: User = {
                  ...user,
                  ...firstAndLastNameFormValues,
                };
                // TODO: use PATCH instead of PUT and only update user's first name rather than the full object
                await optimisticUpdate(updatedUser, null, mutateUser, () => updateUser(userId, updatedUser));
                //updateUser
              }
              close(true);
            } catch (error) {
              logger.error(error);
              // TODO: better error message
              UI.alert(t(translationKeys.error));
            }
          }
          return (
            <>
              {description ? <p>{description}</p> : null}
              <Form vertical schema={memoizedSchema} onSubmit={handleSubmit} submitButton />
            </>
          );
        },
        { closable: options.closable, title }
      );
    } else {
      return Promise.resolve(true);
    }
  }, [
    areRequirementsMissing,
    isLoading,
    options.closable,
    options.initiatedOn,
    memoizedSchema,
    title,
    description,
    requirements,
    mutateUser,
    user,
    isFirstNameRequirementMissing,
    isLastNameRequirementMissing,
    userId,
    t,
  ]);

  return isLoading ? null : launchModal;
}
